import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  RefreshCw, Smartphone, LogOut, ChevronRight, Landmark, Wallet, ClipboardList,
  Layers, ShoppingCart, ArrowUp, ArrowDown, AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';

// Use relative URL for API calls to avoid CORS issues in production
const API = '';

// Costante per il costo di un conto prop
const PROP_ACCOUNT_COST = 750;

// Formatta valuta
const formatCurrency = (value) => {
  const num = parseFloat(value) || 0;
  return `$${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// Calcola deficit deposito (stessa logica del backend)
const calculateDepositDeficit = (user) => {
  const activeCycles = user.cycles?.filter(c => c.status === 'active') || [];
  const nSlotAttivi = activeCycles.length;
  
  if (nSlotAttivi === 0) return 0;
  
  const brokerBalance = parseFloat(user.broker_balance) || 0;
  let sommaPnl = 0;
  
  for (const cycle of activeCycles) {
    const pnl = cycle.broker_pnl_live;
    if (pnl !== null && pnl !== undefined) {
      sommaPnl += parseFloat(pnl);
    } else {
      sommaPnl += 3000; // Default baseline
    }
  }
  
  const soglia = nSlotAttivi * 3000;
  const equityTotale = brokerBalance + sommaPnl;
  const deficit = soglia - equityTotale;
  
  return Math.max(0, Math.round(deficit * 100) / 100);
};

// Stato di priorita' (stesso criterio dell'ordinamento gia' esistente)
const getPriority = (u) => {
  const ready = parseInt(u.ready_accounts) || 0;
  const wallet = parseFloat(u.wallet_balance_usdc) || 0;
  const costs = parseFloat(u.costs_to_recover) || 0;
  if (ready === 0) return 0;               // urgente
  if ((ready === 1 && wallet >= PROP_ACCOUNT_COST) || costs >= PROP_ACCOUNT_COST) return 1; // da fare
  return 2;                                 // ok
};

const PRIORITY_META = [
  { key: 'red', label: 'Urgenti', dot: '#f87171', cls: 'ht-alert' },
  { key: 'yellow', label: 'Da fare', dot: '#f0b45a', cls: 'ht-warn' },
  { key: 'green', label: 'Ok', dot: '#3ddba9', cls: 'ht-pos' },
];

// Slot a trattini: grigio a riposo, verde se attivo
const SlotStatus = ({ cycles }) => {
  const slots = [1, 2, 3, 4].map(slotNum => {
    const cycle = cycles?.find(c => {
      const slotKey = c.arbicsx_slot_key || '';
      if (slotKey.startsWith('slot')) {
        const num = parseInt(slotKey.split('_')[0].replace('slot', ''));
        return num === slotNum;
      }
      return false;
    });
    
    if (!cycle) return { status: 'dormiente', on: false };
    
    switch (cycle.status) {
      case 'active':
        return { status: 'attivo', on: true };
      case 'target_pending':
      case 'target_wallet':
      case 'target_awaiting':
        return { status: 'target', on: true };
      case 'awaiting_payment':
        return { status: 'attesa', on: true };
      default:
        return { status: 'vuoto', on: false };
    }
  });
  
  return (
    <span className="flex items-center gap-1">
      {slots.map((slot, i) => (
        <span
          key={i}
          title={`Slot ${i + 1}: ${slot.status}`}
          className="block h-[3px] w-4 rounded-full"
          style={{ backgroundColor: slot.on ? '#3ddba9' : '#3c424c' }}
        />
      ))}
    </span>
  );
};

const ACTION_ICON = {
  buy: <ShoppingCart size={13} />,
  withdraw: <ArrowUp size={13} />,
  deposit: <ArrowDown size={13} />,
};

// Componente User Card
const UserCard = ({ user, onAction }) => {
  const readyAccounts = parseInt(user.ready_accounts) || 0;
  const walletUsdc = parseFloat(user.wallet_balance_usdc) || 0;
  const costs = parseFloat(user.costs_to_recover) || 0;
  const deficit = calculateDepositDeficit(user);
  
  // Logica bottoni (stessa di _build_user_action_buttons)
  const buttons = [];
  
  // 1. ACQUISTA
  if (readyAccounts === 0 && walletUsdc >= PROP_ACCOUNT_COST) {
    const numAcquistabili = Math.min(Math.floor(walletUsdc / PROP_ACCOUNT_COST), 4);
    for (let i = 1; i <= numAcquistabili; i++) {
      buttons.push({ type: 'buy', label: `Acquista ${i}`, count: i });
    }
  } else if (readyAccounts === 1 && walletUsdc >= PROP_ACCOUNT_COST) {
    buttons.push({ type: 'buy', label: 'Acquista 1', count: 1 });
  }
  
  // 2. PRELEVA
  if ((readyAccounts === 0 && walletUsdc < PROP_ACCOUNT_COST && costs >= PROP_ACCOUNT_COST) ||
      (costs >= PROP_ACCOUNT_COST && readyAccounts >= 1)) {
    buttons.push({ type: 'withdraw', label: 'Preleva' });
  }
  
  // 3. DEPOSITA
  if (deficit > 0 && walletUsdc >= deficit) {
    buttons.push({ type: 'deposit', label: 'Deposita' });
  }
  
  const meta = PRIORITY_META[getPriority(user)];
  
  return (
    <div className="ht-panel p-3 mb-3" data-testid={`mobile-user-${user.id}`}>
      {/* Header con nome e pallino di stato */}
      <div className="flex items-center justify-between gap-2 mb-1">
        <div className="flex items-center gap-2.5 min-w-0">
          <span
            className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-semibold flex-shrink-0 border"
            style={{ color: meta.dot, borderColor: meta.dot + '55', backgroundColor: meta.dot + '14' }}
          >
            {user.name?.charAt(0)?.toUpperCase() || '?'}
          </span>
          <div className="min-w-0">
            <h3 className="ht-acc-title truncate">{user.name || 'Utente'}</h3>
            {user.management_type === 'autonomous' && (
              <span className="ht-item-s">Autonomo</span>
            )}
          </div>
        </div>
        <span
          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
          style={{ backgroundColor: meta.dot }}
          title={meta.label}
        />
      </div>
      
      {/* Righe etichetta / valore */}
      <div className="ht-rows">
        <div className="ht-row">
          <span className="ht-row-label"><Landmark size={13} /> Broker FP</span>
          <span className="ht-row-value">{formatCurrency(user.broker_balance)}</span>
        </div>
        <div className="ht-row">
          <span className="ht-row-label"><Wallet size={13} /> Wallet</span>
          <span className="ht-row-value">
            {formatCurrency(walletUsdc)} <span className="text-[10px] text-[#5f6672]">USDC</span>
          </span>
        </div>
        <div className="ht-row">
          <span className="ht-row-label"><ClipboardList size={13} /> Conti pronti</span>
          <span className={`ht-row-value ${readyAccounts === 0 ? 'ht-alert' : ''}`}>{readyAccounts}</span>
        </div>
        <div className="ht-row">
          <span className="ht-row-label"><Layers size={13} /> Slot</span>
          <SlotStatus cycles={user.cycles} />
        </div>
      </div>
      
      {/* Deficit deposito (se presente) */}
      {deficit > 0 && (
        <div className="mt-2 flex items-center justify-between gap-2 px-3 py-2 rounded-[12px] border border-[rgba(240,180,90,.45)]">
          <span className="text-xs ht-warn flex items-center gap-1.5">
            <AlertCircle size={12} /> Deposito necessario
          </span>
          <span className="ht-item-n ht-warn">{formatCurrency(deficit)}</span>
        </div>
      )}
      
      {/* Action Buttons */}
      {buttons.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-3">
          {buttons.slice(0, 3).map((btn, i) => (
            <button
              key={i}
              type="button"
              onClick={() => onAction(user, btn.type, btn.count)}
              className={`ht-btn flex-1 justify-center ${btn.type === 'buy' ? 'ht-btn-primary' : ''}`}
            >
              {ACTION_ICON[btn.type]}
              <span>{btn.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Componente principale Mobile Dashboard
export default function ArbicsxMobile() {
  const { user: authUser, logout, token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState('all'); // solo lato schermo
  
  const isAdmin = authUser?.email === 'eliabonacci92@gmail.com' || authUser?.role === 'admin';
  
  // Fetch users data
  const fetchUsers = useCallback(async (showRefresh = false) => {
    // Wait for token to be available
    if (!token) {
      console.log('[Mobile] No token available yet');
      return;
    }
    
    if (showRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    
    try {
      const response = await fetch(`${API}/api/arbicsx/users/subscriptions`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Mobile] Error response:', errorText);
        throw new Error(`Errore ${response.status}: ${errorText}`);
      }
      
      const data = await response.json();
      
      // Filtra utenti attivi
      const activeUsers = (data.users || data || []).filter(u => u.is_active !== false);
      
      // Ordina per priority: red > yellow > green
      activeUsers.sort((a, b) => getPriority(a) - getPriority(b));
      
      setUsers(activeUsers);
    } catch (error) {
      console.error('[Mobile] Fetch error:', error);
      setError(error.message);
      toast.error('Errore nel caricamento dei dati');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);
  
  // Fetch when token becomes available
  useEffect(() => {
    if (token) {
      fetchUsers();
    }
  }, [token, fetchUsers]);
  
  // Handle action (placeholder - può essere espanso)
  const handleAction = (user, type, count) => {
    const actionLabels = {
      buy: `Acquista ${count} conto/i`,
      withdraw: 'Preleva',
      deposit: 'Deposita',
    };
    
    toast.info(`${actionLabels[type]} per ${user.name}`, {
      description: 'Usa il bot Telegram o la vista desktop per completare l\'azione.',
      duration: 4000,
    });
  };
  
  // Pull to refresh handler
  const handleRefresh = () => {
    fetchUsers(true);
  };
  
  // Calcola totali
  const totals = users.reduce((acc, u) => ({
    broker: acc.broker + (parseFloat(u.broker_balance) || 0),
    wallet: acc.wallet + (parseFloat(u.wallet_balance_usdc) || 0),
  }), { broker: 0, wallet: 0 });
  
  // Conta per priority
  const counts = users.reduce((acc, u) => {
    const p = getPriority(u);
    if (p === 0) acc.red++;
    else if (p === 1) acc.yellow++;
    else acc.green++;
    return acc;
  }, { red: 0, yellow: 0, green: 0 });
  
  if (loading || !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <div className="w-10 h-10 border-2 border-t-transparent rounded-full animate-spin mx-auto mb-3 border-[#3ddba9]" />
          <p className="text-sm text-[#8b919c]">Caricamento...</p>
        </div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-black">
        <div className="text-center">
          <p className="ht-alert text-sm mb-4">Errore: {error}</p>
          <button type="button" onClick={() => fetchUsers()} className="ht-btn ht-btn-primary mx-auto">
            Riprova
          </button>
        </div>
      </div>
    );
  }
  
  // Filtro e raggruppamento: SOLO lato schermo, l'ordine di priorita' resta quello di sopra
  const groups = PRIORITY_META.map((meta, idx) => ({
    ...meta,
    users: users.filter(u => getPriority(u) === idx),
  })).filter(g => filter === 'all' || g.key === filter);
  
  const FILTERS = [
    { key: 'all', label: 'Tutti', count: users.length, dot: null },
    { key: 'red', label: 'urgenti', count: counts.red, dot: '#f87171' },
    { key: 'yellow', label: 'da fare', count: counts.yellow, dot: '#f0b45a' },
    { key: 'green', label: 'ok', count: counts.green, dot: '#3ddba9' },
  ];
  
  return (
    <div className="min-h-screen pb-8 bg-black">
      {/* Header */}
      <header className="sticky top-0 z-50 px-4 py-3 flex items-center justify-between bg-[#0d1117] border-b border-[#282c34]">
        <div className="flex items-center gap-2 min-w-0">
          <Smartphone size={18} className="ht-pos flex-shrink-0" />
          <span className="font-bold text-white font-heading text-sm truncate">HedgingTracker</span>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={refreshing}
            className="ht-btn ht-btn-icon"
            aria-label="Aggiorna"
            data-testid="mobile-refresh-btn"
          >
            <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
          </button>
          <button
            type="button"
            onClick={logout}
            className="ht-btn ht-btn-icon"
            aria-label="Esci"
            data-testid="mobile-logout-btn"
          >
            <LogOut size={15} />
          </button>
        </div>
      </header>
      
      {/* Riepilogo sempre in alto + filtri per stato */}
      <div className="sticky top-[57px] z-40 bg-black px-4 pt-3 pb-2 border-b border-[#282c34]">
        <div className="flex items-end justify-between gap-3">
          <div className="min-w-0">
            <span className="ht-cell-label">Totale Broker</span>
            <p className="ht-item-n ht-pos text-lg">{formatCurrency(totals.broker)}</p>
          </div>
          <div className="min-w-0 text-right">
            <span className="ht-cell-label">Totale Wallet</span>
            <p className="ht-item-n text-lg">{formatCurrency(totals.wallet)}</p>
          </div>
        </div>
        <div className="flex gap-1.5 mt-2 overflow-x-auto no-scrollbar">
          {FILTERS.map(f => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className="ht-tab flex-shrink-0"
              data-state={filter === f.key ? 'active' : 'inactive'}
              data-testid={`mobile-filter-${f.key}`}
            >
              {f.dot && <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: f.dot }} />}
              {f.key === 'all' ? f.label : `${f.count} ${f.label}`}
            </button>
          ))}
        </div>
      </div>
      
      <div className="px-4 py-3">
        {/* User greeting for non-admin */}
        {!isAdmin && users.length === 1 && (
          <p className="text-xs text-[#8b919c] mb-3 text-center">
            Ciao {users[0]?.name?.split(' ')[0] || 'utente'}! Ecco il tuo riepilogo.
          </p>
        )}
        
        {/* Utenti raggruppati per stato */}
        {users.length === 0 ? (
          <div className="ht-empty">Nessun utente trovato</div>
        ) : (
          groups.map(group => (
            group.users.length > 0 && (
              <div key={group.key} className="mb-4">
                <p className="ht-cell-label flex items-center gap-1.5 mb-2">
                  <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: group.dot }} />
                  {group.label}
                </p>
                {group.users.map(user => (
                  <UserCard key={user.id} user={user} onAction={handleAction} />
                ))}
              </div>
            )
          ))
        )}
      </div>
      
      {/* Footer */}
      <div className="text-center py-4">
        <p className="text-xs text-[#5f6672]">
          HedgingTracker Mobile v1.0
        </p>
        <a 
          href="/arbicsx/utenti?desktop=1" 
          className="text-xs ht-pos flex items-center justify-center gap-1 mt-2"
        >
          Apri vista desktop <ChevronRight size={13} />
        </a>
      </div>
    </div>
  );
}
